import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Settings, Send, Mail, Phone, RefreshCw, ChevronLeft, ChevronDown, History, MessageCircle, Check, CheckCheck, Circle, Loader2, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getMessageLoadLimit } from './NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

// Construct WebSocket URL properly
const getWebSocketUrl = () => {
  const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
  const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${wsHost}`;
};

/**
 * Determine conversation type based on sender and recipient roles
 */
const getConversationType = (senderRole, recipientRole) => {
  if (senderRole === 'super_admin' && recipientRole === 'calendar_admin') {
    return 'super_to_calendar';
  }
  if (senderRole === 'calendar_admin' && recipientRole === 'super_admin') {
    return 'calendar_to_super';
  }
  if (senderRole === 'calendar_admin' && (recipientRole === 'customer' || recipientRole === 'non_registered_customer')) {
    return 'calendar_to_customer';
  }
  if (senderRole === 'customer' && recipientRole === 'calendar_admin') {
    return 'customer_to_calendar';
  }
  return 'calendar_to_customer';
};

// Local storage keys
const getSubjectStorageKey = (userId, conversationType) => 
  `messageConsole_defaultSubject_${userId}_${conversationType}`;
const getSubjectHistoryKey = (userId, conversationType) => 
  `messageConsole_subjectHistory_${userId}_${conversationType}`;
const getChannelStorageKey = (userId, recipientId) =>
  `messageConsole_channel_${userId}_${recipientId}`;

const MAX_SUBJECT_HISTORY = 20;

/**
 * MessageConsole - Unified message thread showing ALL channels
 * Channel selector only affects how NEW messages are sent
 * Supports infinite scroll to load older messages
 * 
 * Props:
 * - customer: The chat partner object
 * - isOpen: Whether the console is visible
 * - onClose: Callback when console closes
 * - onMessagesLoaded: Callback when messages are loaded
 * - messageType: Optional filter - 'support' or 'regular' (default: all messages)
 */
const MessageConsole = ({ customer, isOpen, onClose, onMessagesLoaded, messageType = null }) => {
  const { getAuthHeaders, runningUser, loginUser, token, isRunAsMode } = useAuth();
  const [messages, setMessages] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  
  // Infinite scroll state
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [oldestTimestamp, setOldestTimestamp] = useState(null);
  
  // Conversation type
  const [conversationType, setConversationType] = useState('calendar_to_customer');
  
  // Message input
  const [newMessage, setNewMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [defaultSubjectInitialized, setDefaultSubjectInitialized] = useState(false);
  
  // Email expansion state - track which email messages are expanded
  const [expandedEmails, setExpandedEmails] = useState(new Set());
  
  // Subject history
  const [subjectHistory, setSubjectHistory] = useState([]);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showSubjectHistoryModal, setShowSubjectHistoryModal] = useState(false);
  const subjectDropdownRef = useRef(null);
  
  // Settings & Channel (for SENDING only)
  const [showSettings, setShowSettings] = useState(false);
  const [channel, setChannel] = useState('yibooking');
  const [settings, setSettings] = useState({ imap_fetch_frequency: 5 });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // WebSocket & Online Status
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [recipientOnline, setRecipientOnline] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // For infinite scroll detection
  const inputRef = useRef(null);
  const onMessagesLoadedRef = useRef(onMessagesLoaded); // Callback ref for messages loaded
  const initialLoadDoneRef = useRef(false); // Prevent multiple initial loads
  const wsInitializedRef = useRef(false); // Prevent multiple WS connections

  // Keep callback ref updated
  onMessagesLoadedRef.current = onMessagesLoaded;

  // Determine conversation type
  useEffect(() => {
    if (runningUser?.role && customer?.role) {
      const type = getConversationType(runningUser.role, customer.role);
      setConversationType(type);
    }
  }, [runningUser?.role, customer?.role]);

  // Get default subject based on conversation type
  const getDefaultSubjectForType = useCallback((type) => {
    switch (type) {
      case 'super_to_calendar':
        return 'Message from yiBooking.com';
      case 'calendar_to_super':
        return `Message from ${runningUser?.full_name || 'Calendar Admin'}`;
      case 'calendar_to_customer':
        return `Message from ${runningUser?.full_name || 'Calendar Admin'}`;
      case 'customer_to_calendar':
        return `Message from ${runningUser?.full_name || 'Customer'}`;
      default:
        return `Message from ${runningUser?.full_name || 'Admin'}`;
    }
  }, [runningUser?.full_name]);

  // Load saved channel preference
  useEffect(() => {
    if (runningUser?.id && customer?.id) {
      const savedChannel = localStorage.getItem(getChannelStorageKey(runningUser.id, customer.id));
      if (savedChannel && ['yibooking', 'email', 'sms'].includes(savedChannel)) {
        setChannel(savedChannel);
      }
    }
  }, [runningUser?.id, customer?.id]);

  // Save channel preference
  const handleChannelChange = (newChannel) => {
    setChannel(newChannel);
    if (runningUser?.id && customer?.id) {
      localStorage.setItem(getChannelStorageKey(runningUser.id, customer.id), newChannel);
    }
  };

  // Load subject history
  const loadSubjectHistory = useCallback(() => {
    if (!runningUser?.id || !conversationType) return;
    const historyKey = getSubjectHistoryKey(runningUser.id, conversationType);
    const stored = localStorage.getItem(historyKey);
    if (stored) {
      try {
        setSubjectHistory(JSON.parse(stored) || []);
      } catch {
        setSubjectHistory([]);
      }
    } else {
      setSubjectHistory([]);
    }
  }, [runningUser?.id, conversationType]);

  // Save subject to history
  const addSubjectToHistory = useCallback((newSubject) => {
    if (!runningUser?.id || !newSubject.trim() || !conversationType) return;
    const historyKey = getSubjectHistoryKey(runningUser.id, conversationType);
    const updatedHistory = [
      newSubject.trim(),
      ...subjectHistory.filter(s => s !== newSubject.trim())
    ].slice(0, MAX_SUBJECT_HISTORY);
    setSubjectHistory(updatedHistory);
    localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
  }, [runningUser?.id, conversationType, subjectHistory]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subjectDropdownRef.current && !subjectDropdownRef.current.contains(event.target)) {
        setShowSubjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle WebSocket messages - define before useEffect that uses it
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        // Connected successfully
        break;
        
      case 'message':
        if (data.message && (data.message.sender_id === customer?.id || data.message.recipient_id === customer?.id)) {
          setMessages(prev => {
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
        break;
        
      case 'message_sent':
        if (data.message) {
          setMessages(prev => prev.map(m => 
            m.id === data.message.id ? data.message : m
          ));
        }
        break;
        
      case 'typing':
        if (data.sender_id === customer?.id) {
          setIsTyping(data.is_typing);
        }
        break;
        
      case 'read_receipt':
        if (data.message_ids && data.reader_id === customer?.id) {
          setMessages(prev => prev.map(m => 
            data.message_ids.includes(m.id) 
              ? { ...m, read_at: data.read_at, status: 'read' }
              : m
          ));
        }
        break;
        
      case 'online_status':
        if (data.user_id === customer?.id) {
          setRecipientOnline(data.is_online);
        }
        break;
        
      case 'online_status_response':
        if (data.statuses && data.statuses[customer?.id]) {
          setRecipientOnline(data.statuses[customer.id].is_online);
        }
        break;
        
      case 'error':
        console.error('WebSocket error:', data.message);
        break;
        
      default:
        break;
    }
  }, [customer?.id]);

  // WebSocket connection - only connect once per session
  useEffect(() => {
    if (!isOpen || !token || !customer?.id || wsInitializedRef.current) return;
    
    wsInitializedRef.current = true;
    
    const connectWebSocket = () => {
      try {
        const wsUrl = `${getWebSocketUrl()}/api/ws/chat?token=${token}`;
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          setWsConnected(true);
          ws.send(JSON.stringify({
            type: 'get_online_status',
            user_ids: [customer.id]
          }));
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleWebSocketMessage(data);
          } catch (err) {
            // Silent error
          }
        };
        
        ws.onerror = () => {
          setWsConnected(false);
        };
        
        ws.onclose = () => {
          setWsConnected(false);
        };
        
        wsRef.current = ws;
      } catch (err) {
        // WebSocket is optional
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      wsInitializedRef.current = false;
    };
  }, [isOpen, token, customer?.id, handleWebSocketMessage]);

  // Fetch messages (initial load or pagination for older messages)
  const fetchMessages = useCallback(async (beforeTimestamp = null) => {
    if (!customer?.id) return;
    
    const isInitialLoad = !beforeTimestamp;
    
    if (isInitialLoad) {
      setLoading(true);
      setHasMoreMessages(true);
      setOldestTimestamp(null);
    } else {
      setLoadingOlder(true);
    }
    setError('');
    
    try {
      const loadLimit = getMessageLoadLimit();
      const params = { limit: loadLimit };
      if (beforeTimestamp) {
        params.before = beforeTimestamp;
      }
      // Add message_type filter if specified
      if (messageType) {
        params.message_type = messageType;
      }
      
      const response = await axios.get(`${API_BASE_URL}/messages/${customer.id}`, {
        params,
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        const fetchedMessages = response.data.data.messages || [];
        
        if (isInitialLoad) {
          setMessages(fetchedMessages);
          setCustomerInfo(response.data.data.customer);
          // Notify parent that messages loaded successfully
          onMessagesLoadedRef.current?.();
        } else {
          // Prepend older messages (they come sorted oldest first)
          setMessages(prev => {
            // Filter out duplicates
            const existingIds = new Set(prev.map(m => m.id));
            const newMessages = fetchedMessages.filter(m => !existingIds.has(m.id));
            return [...newMessages, ...prev];
          });
        }
        
        // Update pagination state
        if (fetchedMessages.length < loadLimit) {
          setHasMoreMessages(false);
        }
        
        // Track oldest timestamp for next pagination
        if (fetchedMessages.length > 0) {
          const oldest = fetchedMessages[0]; // First message is oldest (sorted asc)
          setOldestTimestamp(oldest.timestamp);
        }
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setLoadingOlder(false);
      }
    }
  }, [customer?.id, getAuthHeaders]);

  // Load older messages (for infinite scroll)
  const loadOlderMessages = useCallback(() => {
    if (loadingOlder || !hasMoreMessages || !oldestTimestamp) return;
    fetchMessages(oldestTimestamp);
  }, [loadingOlder, hasMoreMessages, oldestTimestamp, fetchMessages]);

  // Infinite scroll handler - detect when user scrolls to top
  const handleScroll = useCallback((e) => {
    const container = e.target;
    // Trigger load when scrolled near top (within 50px)
    if (container.scrollTop < 50 && hasMoreMessages && !loadingOlder && !loading) {
      loadOlderMessages();
    }
  }, [hasMoreMessages, loadingOlder, loading, loadOlderMessages]);

  // Poll IMAP and refresh
  const pollAndRefresh = useCallback(async () => {
    if (!customer?.id) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Poll IMAP for new emails
      try {
        await axios.post(
          `${API_BASE_URL}/messages/${customer.id}/poll-imap`,
          {},
          { headers: getAuthHeaders() }
        );
      } catch (pollErr) {
        console.warn('IMAP poll failed:', pollErr.response?.data?.detail || pollErr.message);
      }
      
      // Refresh all messages
      const response = await axios.get(`${API_BASE_URL}/messages/${customer.id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        setMessages(response.data.data.messages || []);
        setCustomerInfo(response.data.data.customer);
      }
    } catch (err) {
      console.error('Error refreshing messages:', err);
      setError('Failed to refresh messages');
    } finally {
      setLoading(false);
    }
  }, [customer?.id, getAuthHeaders]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!customer?.id) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/${customer.id}/settings`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, [customer?.id, getAuthHeaders]);

  // Handle subject change
  const handleSubjectChange = (newSubject) => {
    setSubject(newSubject);
    if (runningUser?.id && conversationType) {
      localStorage.setItem(getSubjectStorageKey(runningUser.id, conversationType), newSubject);
    }
  };

  const handleSelectSubjectFromHistory = (selectedSubject) => {
    handleSubjectChange(selectedSubject);
    setShowSubjectDropdown(false);
    setShowSubjectHistoryModal(false);
  };

  // Load data when opened - only once per open session
  useEffect(() => {
    if (isOpen && customer?.id && !initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      // Reset pagination state
      setHasMoreMessages(true);
      setOldestTimestamp(null);
      setMessages([]);
      // Fetch initial messages
      fetchMessages();
      fetchSettings();
      loadSubjectHistory();
      setNewMessage('');
      setError('');
      setDefaultSubjectInitialized(false);
      setSubject('');
    }
    
    // Reset when component unmounts or closes
    return () => {
      if (!isOpen) {
        initialLoadDoneRef.current = false;
      }
    };
  }, [isOpen, customer?.id, fetchMessages, fetchSettings, loadSubjectHistory]);

  // Initialize default subject
  useEffect(() => {
    if (isOpen && runningUser?.id && conversationType && !defaultSubjectInitialized) {
      const storageKey = getSubjectStorageKey(runningUser.id, conversationType);
      const savedSubject = localStorage.getItem(storageKey);
      
      if (savedSubject) {
        setSubject(savedSubject);
      } else {
        const defaultSubject = getDefaultSubjectForType(conversationType);
        setSubject(defaultSubject);
        localStorage.setItem(storageKey, defaultSubject);
      }
      setDefaultSubjectInitialized(true);
    }
  }, [isOpen, runningUser?.id, conversationType, defaultSubjectInitialized, getDefaultSubjectForType]);

  // Load subject history when conversation type changes
  useEffect(() => {
    if (isOpen && runningUser?.id && conversationType) {
      loadSubjectHistory();
    }
  }, [isOpen, runningUser?.id, conversationType, loadSubjectHistory]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !loading) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, loading]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((typing) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && channel === 'yibooking') {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        recipient_id: customer?.id,
        is_typing: typing
      }));
    }
  }, [customer?.id, channel]);

  // Handle typing with debounce
  const handleTyping = useCallback(() => {
    if (channel !== 'yibooking') return;
    sendTypingIndicator(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => sendTypingIndicator(false), 2000);
  }, [sendTypingIndicator, channel]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    if (channel === 'email' && !subject.trim()) {
      setError('Subject is required for email messages');
      return;
    }
    
    setSending(true);
    setError('');
    sendTypingIndicator(false);
    
    try {
      if (channel === 'yibooking' && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        // Send via WebSocket
        const tempId = `temp_${Date.now()}`;
        const tempMessage = {
          id: tempId,
          sender_id: runningUser?.id,
          recipient_id: customer?.id,
          content: newMessage,
          subject: subject || null,
          channel: 'yibooking',
          timestamp: new Date().toISOString(),
          status: 'sending',
          direction: 'outbound'
        };
        
        setMessages(prev => [...prev, tempMessage]);
        setNewMessage('');
        
        // Build WebSocket message - include run_as_user_id if in Run As mode
        const wsMessage = {
          type: 'message',
          recipient_id: customer.id,
          content: newMessage,
          subject: subject || '',
          message_type: messageType || 'regular'  // Include message_type for filtering
        };
        
        // If in Run As mode, tell backend who we're running as
        if (isRunAsMode && isRunAsMode() && runningUser?.id !== loginUser?.id) {
          wsMessage.run_as_user_id = runningUser.id;
        }
        
        wsRef.current.send(JSON.stringify(wsMessage));
        
        setTimeout(() => {
          setMessages(prev => prev.map(m => 
            m.id === tempId ? { ...m, status: 'sent' } : m
          ));
        }, 500);
        
      } else {
        // Send via REST API (email/sms)
        const response = await axios.post(
          `${API_BASE_URL}/messages/${customer.id}`,
          {
            subject: channel === 'email' ? subject : null,
            content: newMessage,
            channel: channel,
            message_type: messageType || 'regular'  // Include message_type for filtering
          },
          { headers: getAuthHeaders() }
        );
        
        if (response.data.success) {
          setMessages(prev => [...prev, response.data.data]);
          setNewMessage('');
          if (channel === 'email' && subject.trim()) {
            addSubjectToHistory(subject);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${API_BASE_URL}/messages/${customer.id}/settings`,
        settings,
        { headers: getAuthHeaders() }
      );
      setShowSettings(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Clean up inbound email content
  const cleanEmailContent = (content, direction, msgChannel) => {
    if (!content || direction !== 'inbound' || msgChannel !== 'email') return content;
    
    let cleaned = content;
    const signaturePatterns = [
      /Sent with \[?Proton Mail\]?.*$/gim,
      /Sent from my iPhone.*$/gim,
      /Sent from my Android.*$/gim,
      /--\s*\n[\s\S]*$/gm,
    ];
    
    for (const pattern of signaturePatterns) {
      cleaned = cleaned.replace(pattern, '');
    }
    
    cleaned = cleaned.replace(/On\s+.+wrote:\s*$/gim, '');
    cleaned = cleaned.split('\n').filter(line => !line.trim().startsWith('>')).join('\n');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    
    return cleaned || content;
  };

  // Get message status icon
  const getStatusIcon = (msg) => {
    if (msg.direction !== 'outbound') return null;
    if (msg.status === 'sending') return <Loader2 className="w-3 h-3 animate-spin" />;
    if (msg.status === 'failed') return <span className="text-red-500 text-xs">!</span>;
    if (msg.read_at || msg.status === 'read') return <CheckCheck className="w-3 h-3 text-blue-500" />;
    return <Check className="w-3 h-3" />;
  };

  // Get channel icon
  const getChannelIcon = (msgChannel) => {
    switch (msgChannel) {
      case 'yibooking': return <MessageCircle className="w-3 h-3" />;
      case 'email': return <Mail className="w-3 h-3" />;
      case 'sms': return <Phone className="w-3 h-3" />;
      default: return <Mail className="w-3 h-3" />;
    }
  };

  // Toggle email expansion
  const toggleEmailExpanded = (msgId) => {
    setExpandedEmails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(msgId)) {
        newSet.delete(msgId);
      } else {
        newSet.add(msgId);
      }
      return newSet;
    });
  };

  // Get conversation type label
  const getConversationTypeLabel = () => {
    switch (conversationType) {
      case 'super_to_calendar': return 'Calendar Admin';
      case 'calendar_to_super': return 'Super Admin';
      case 'calendar_to_customer':
        return customerInfo?.role === 'non_registered_customer' ? 'Non-Registered' : 'Customer';
      case 'customer_to_calendar': return 'Calendar Admin';
      default: return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />
      
      {/* Slide-out Panel */}
      <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center">
            <button onClick={onClose} className="mr-3 p-1 hover:bg-gray-200 rounded-full transition-colors">
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-gray-800">
                  {customerInfo?.full_name || customer?.full_name || 'Messages'}
                </h2>
                <Circle 
                  className={`w-2.5 h-2.5 ${recipientOnline ? 'text-green-500 fill-green-500' : 'text-gray-300 fill-gray-300'}`}
                  title={recipientOnline ? 'Online' : 'Offline'}
                />
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  customerInfo?.role === 'non_registered_customer' || customer?.role === 'non_registered_customer'
                    ? 'bg-orange-100 text-orange-700'
                    : customerInfo?.role === 'calendar_admin' || customer?.role === 'calendar_admin'
                      ? 'bg-purple-100 text-purple-700'
                      : customerInfo?.role === 'super_admin' || customer?.role === 'super_admin'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                }`}>
                  {getConversationTypeLabel()}
                </span>
              </div>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                {customerInfo?.email || customer?.email}
                {wsConnected && <span className="text-green-500">• Live</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={pollAndRefresh}
              disabled={loading}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50"
              title="Refresh messages"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 hover:bg-gray-200 rounded-full transition-colors ${showSettings ? 'bg-gray-200' : ''}`}
              title="Settings"
            >
              <Settings className="w-5 h-5 text-gray-600" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="p-4 border-b bg-yellow-50 space-y-3">
            <h3 className="font-medium text-gray-800">Settings</h3>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Email Fetch Frequency</label>
              <select
                value={settings.imap_fetch_frequency}
                onChange={(e) => setSettings({...settings, imap_fetch_frequency: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
              >
                <option value={1}>Every 1 minute</option>
                <option value={5}>Every 5 minutes</option>
                <option value={15}>Every 15 minutes</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md disabled:bg-yellow-300"
              >
                {savingSettings ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-100 border-b border-red-200 text-red-700 text-sm">
            {error}
            <button onClick={() => setError('')} className="float-right text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Messages Area - MERGED VIEW (all channels) with infinite scroll */}
        <div 
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
        >
          {/* Load older messages indicator */}
          {loadingOlder && (
            <div className="flex items-center justify-center py-2">
              <RefreshCw className="w-5 h-5 text-yellow-500 animate-spin mr-2" />
              <span className="text-sm text-gray-500">Loading older messages...</span>
            </div>
          )}
          
          {/* "Load more" button when scrolled to top but not auto-loading */}
          {!loadingOlder && hasMoreMessages && messages.length > 0 && !loading && (
            <button
              onClick={loadOlderMessages}
              className="w-full py-2 text-sm text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-md flex items-center justify-center gap-1 transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
              Load older messages
            </button>
          )}
          
          {/* End of history indicator */}
          {!hasMoreMessages && messages.length > 0 && (
            <div className="text-center py-2 text-xs text-gray-400">
              — Beginning of conversation —
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-8 h-8 text-yellow-500 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <MessageCircle className="w-12 h-12 mb-2 text-gray-300" />
              <p className="text-center px-4">
                This is the beginning of conversation with {customerInfo?.full_name || customer?.full_name || 'this user'}
              </p>
              <p className="text-sm mt-1">Start the conversation below</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isNonRegisteredInbound = msg.direction === 'inbound' && 
                (customerInfo?.role === 'non_registered_customer' || customer?.role === 'non_registered_customer');
              const msgChannel = msg.channel || 'email';
              
              return (
                <div
                  key={msg.id}
                  className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.direction === 'outbound'
                        ? msgChannel === 'yibooking' 
                          ? 'bg-yellow-100 text-gray-800 rounded-br-none'
                          : 'bg-green-100 text-gray-800 rounded-br-none'
                        : isNonRegisteredInbound
                          ? 'bg-pink-100 text-gray-800 rounded-bl-none'
                          : msgChannel === 'yibooking'
                            ? 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                            : 'bg-blue-100 text-gray-800 rounded-bl-none'
                    }`}
                  >
                    {/* Email messages - collapsible with subject header */}
                    {msgChannel === 'email' ? (
                      <>
                        {/* Email Header - always visible */}
                        <button
                          onClick={() => toggleEmailExpanded(msg.id)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              {/* Subject */}
                              <div className={`text-sm font-medium ${
                                msg.direction === 'outbound' ? 'text-green-700' : isNonRegisteredInbound ? 'text-pink-700' : 'text-blue-700'
                              }`}>
                                {msg.subject || '(No Subject)'}
                              </div>
                              {/* Preview - show first line when collapsed */}
                              {!expandedEmails.has(msg.id) && msg.content && (
                                <p className="text-xs text-gray-500 mt-1 truncate">
                                  {cleanEmailContent(msg.content, msg.direction, msgChannel).split('\n')[0].substring(0, 60)}
                                  {cleanEmailContent(msg.content, msg.direction, msgChannel).length > 60 && '...'}
                                </p>
                              )}
                            </div>
                            {/* Expand/Collapse indicator */}
                            <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${
                              expandedEmails.has(msg.id) ? 'rotate-180' : ''
                            } ${
                              msg.direction === 'outbound' ? 'text-green-600' : isNonRegisteredInbound ? 'text-pink-600' : 'text-blue-600'
                            }`} />
                          </div>
                        </button>
                        
                        {/* Email Content - shown when expanded */}
                        {expandedEmails.has(msg.id) && (
                          <div className="mt-2 pt-2 border-t border-opacity-30 border-current">
                            <p className="text-sm whitespace-pre-wrap">
                              {cleanEmailContent(msg.content, msg.direction, msgChannel)}
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Non-email messages (yibooking, SMS) - show content directly */
                      <p className="text-sm whitespace-pre-wrap">
                        {cleanEmailContent(msg.content, msg.direction, msgChannel)}
                      </p>
                    )}
                    
                    {/* Timestamp, Channel Icon & Status */}
                    <div className={`flex items-center justify-end gap-1 mt-2 text-xs ${
                      msg.direction === 'outbound' 
                        ? msgChannel === 'yibooking' ? 'text-yellow-600' : 'text-green-600'
                        : isNonRegisteredInbound ? 'text-pink-600' : msgChannel === 'yibooking' ? 'text-gray-500' : 'text-blue-600'
                    }`}>
                      {getChannelIcon(msgChannel)}
                      <span className="ml-1">{formatTime(msg.timestamp)}</span>
                      {getStatusIcon(msg)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          
          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600">
                typing...
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t bg-white p-4">
          {/* Channel Selector (for SENDING) */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-gray-500">Send via:</span>
            <button
              onClick={() => handleChannelChange('yibooking')}
              className={`flex items-center px-2 py-1 rounded text-xs transition-colors ${
                channel === 'yibooking'
                  ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              yiBooking
            </button>
            <button
              onClick={() => handleChannelChange('email')}
              className={`flex items-center px-2 py-1 rounded text-xs transition-colors ${
                channel === 'email'
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Mail className="w-3 h-3 mr-1" />
              Email
            </button>
            <button
              onClick={() => handleChannelChange('sms')}
              disabled={!customerInfo?.accepts_sms && !customer?.accepts_sms}
              className={`flex items-center px-2 py-1 rounded text-xs transition-colors ${
                channel === 'sms'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${!customerInfo?.accepts_sms && !customer?.accepts_sms ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Phone className="w-3 h-3 mr-1" />
              SMS
            </button>
          </div>
          
          <form onSubmit={handleSendMessage} className="space-y-2">
            {/* Subject field for email */}
            {channel === 'email' && (
              <div className="relative" ref={subjectDropdownRef}>
                <div className="flex">
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    placeholder="Subject..."
                    className={`flex-1 px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm ${
                      subjectHistory.length > 0 ? 'rounded-l-md' : 'rounded-md'
                    }`}
                  />
                  {subjectHistory.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowSubjectDropdown(!showSubjectDropdown)}
                      className="px-2 py-2 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-100"
                    >
                      <ChevronDown className={`w-4 h-4 text-gray-500 ${showSubjectDropdown ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                
                {showSubjectDropdown && subjectHistory.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-hidden">
                    {subjectHistory.slice(0, 5).map((historySubject, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectSubjectFromHistory(historySubject)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 truncate border-b border-gray-100 last:border-0"
                      >
                        {historySubject}
                      </button>
                    ))}
                    {subjectHistory.length > 5 && (
                      <button
                        type="button"
                        onClick={() => { setShowSubjectDropdown(false); setShowSubjectHistoryModal(true); }}
                        className="w-full px-3 py-2 text-left text-sm text-yellow-600 hover:bg-yellow-50 font-medium flex items-center gap-2"
                      >
                        <History className="w-4 h-4" />
                        More ({subjectHistory.length - 5})
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-end space-x-2">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => { setNewMessage(e.target.value); handleTyping(); }}
                  placeholder="Type your message..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm resize-none"
                  rows={2}
                  maxLength={channel === 'sms' ? 160 : undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                {channel === 'sms' && (
                  <p className="text-xs text-gray-400 mt-1">{newMessage.length}/160</p>
                )}
              </div>
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 flex items-center ${
                  channel === 'yibooking' ? 'bg-yellow-500 hover:bg-yellow-600' 
                    : channel === 'email' ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : (
                  <><Send className="w-5 h-5 mr-1" /><span className="hidden sm:inline">Send</span></>
                )}
              </button>
            </div>
          </form>
          
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
            <span className="flex items-center">
              {channel === 'yibooking' ? (
                <><MessageCircle className="w-3 h-3 mr-1" /> yiBooking {recipientOnline && <span className="ml-1 text-green-500">• Online</span>}</>
              ) : channel === 'email' ? (
                <><Mail className="w-3 h-3 mr-1" /> Email</>
              ) : (
                <><Phone className="w-3 h-3 mr-1" /> SMS</>
              )}
            </span>
            <span>Enter to send</span>
          </div>
        </div>
      </div>

      {/* Subject History Modal */}
      {showSubjectHistoryModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowSubjectHistoryModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <History className="w-5 h-5 text-yellow-500" />
                Recent Subjects
              </h3>
              <button onClick={() => setShowSubjectHistoryModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {subjectHistory.map((historySubject, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectSubjectFromHistory(historySubject)}
                  className="w-full px-4 py-3 text-left text-sm hover:bg-yellow-50 rounded-md border-b border-gray-100 last:border-0"
                >
                  {historySubject}
                </button>
              ))}
            </div>
            <div className="p-3 border-t bg-gray-50 text-center">
              <button onClick={() => setShowSubjectHistoryModal(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageConsole;
